import { api } from '@/lib/axios'
import type { ExportRequest } from '@/lib/export-request'

// ─── AP-20: Manager-Abgabeübersicht ───────────────────────────────────────────

export type MonthlyReportRowStatus = 'submitted' | 'open' | 'not_required'
export type MonthlyReportSubmissionStatus = 'SUBMITTED' | 'REVOKED'
export type PayrollProcessStatus = 'RELEASED' | 'IN_PAYROLL' | 'PROCESSED' | 'CLOSED'

/** AP-24: Woher die Stunden des Monatsberichts kommen. */
export type MonthlyReportTimeSource = 'TIME_ENTRIES' | 'MANUAL'

export interface MonthlyReportOverviewRow {
  userId: string
  name: string
  email: string
  /** AP-30 Stufe 3: zugewiesene Rollen statt eines Rangs. */
  roles: { key: string; name: string }[]
  required: boolean
  /** Aufgelöst: was für diesen Mitarbeiter tatsächlich gilt. */
  timeSource: MonthlyReportTimeSource
  /** true = eigene Übersteuerung, false = folgt dem Mandanten-Standard. */
  timeSourceOverridden: boolean
  status: MonthlyReportRowStatus
  submission: {
    id: string
    status: MonthlyReportSubmissionStatus
    submittedAt: string
    payrollStatus: PayrollProcessStatus
    contentHash: string
  } | null
}

export interface MonthlyReportOverview {
  period: { year: number; month: number; label: string }
  tenantTimeSource: MonthlyReportTimeSource
  counts: { total: number; required: number; submitted: number; open: number }
  rows: MonthlyReportOverviewRow[]
}

// ─── AP-20: Eigener Monatsbericht (Mitarbeiter-Abgabe, wie in der PWA) ─────────

export type MonthlyReportHoursFormat = 'decimal' | 'hhmm'

/**
 * Spaltenart. `time` und `number` sind mit AP-26 dazugekommen:
 * `time` ist eine fertige HH:MM-Zeichenkette (nicht summierbar), `number` eine
 * ganze Zahl ohne Stundenformat.
 */
export type MonthlyReportColumnKind = 'hours' | 'text' | 'boolean' | 'time' | 'number'

export interface MonthlyReportColumn {
  key: string
  label: string
  kind: MonthlyReportColumnKind
  align: 'left' | 'center' | 'right'
  summable: boolean
  isLv: boolean
  /** AP-24: Überschrift in Bildschirm-Tabellen weglassen (z.B. Wochentag). */
  headerHidden?: boolean
}

export interface MonthlyReportRow {
  date: string
  weekday: string
  grossMinutes: number
  breakMinutes: number
  netMinutes: number
  absenceMinutes: number
  absenceDescription: string
  totalWithAbsenceMinutes: number
  lv: Record<string, unknown>
  /** AP-24: woher die Stunden dieser Zeile stammen. */
  source: 'timeEntries' | 'manual'

  // ── AP-26: Uhrzeiten und Montage-/Reisefelder ──────────────────────────────
  // Die Zeitfelder kommen bereits als HH:MM vom Server (Wall-Clock als UTC) —
  // hier wird nicht noch einmal formatiert.
  startTime: string
  endTime: string
  departure: string
  returnTime: string
  travelMinutes: number
  km: number
  isDriver: boolean
  overnight: boolean
  appointment: string
  appointmentMinutes: number
}

export interface MonthlyReportTotals {
  grossMinutes: number
  breakMinutes: number
  netMinutes: number
  absenceMinutes: number
  totalWithAbsenceMinutes: number
  lv: Record<string, number>
  // AP-26: Reisezeit/Termindauer in Minuten, Kilometer als Zahl,
  // Lenker/Naechtigung als **Anzahl der Ja-Tage**.
  travelMinutes: number
  km: number
  isDriver: number
  overnight: number
  appointmentMinutes: number
}

// ─── AP-26 Phase B: Spaltenkatalog vom Server ────────────────────────────────

/**
 * Eine Spalte, wie sie die Monatsbericht-Einrichtung anbietet.
 *
 * Bis AP-26 führte der ERP diesen Katalog als eigene Konstante — dieselbe Liste
 * wie im Backend, gekoppelt nur durch einen Kommentar. Seit AP-26 kommt sie vom
 * Server: Sie hängt ohnehin von den Zusatzfeldern des Mandanten ab, und neue
 * Spalten sollen hier ohne Frontend-Änderung erscheinen.
 */
export interface MonthlyReportCatalogColumn {
  key: string
  label: string
  kind: MonthlyReportColumnKind
  align: 'left' | 'center' | 'right'
  summable: boolean
  isLv: boolean
  /** Vorgabe, solange der Mandant keine Spaltenauswahl gespeichert hat. */
  defaultEnabled: boolean
}

export interface MonthlyReportColumnCatalog {
  columns: MonthlyReportCatalogColumn[]
  config: {
    hoursFormat: MonthlyReportHoursFormat
    confirmationText: string
    columns: { key: string; enabled: boolean; order: number }[]
    timeSource: MonthlyReportTimeSource
  }
}

export interface MonthlyReportSubmissionInfo {
  id: string
  status: MonthlyReportSubmissionStatus
  submittedAt: string
  payrollStatus: PayrollProcessStatus
  contentHash: string
}

export interface MonthlyReport {
  period: { year: number; month: number; from: string; to: string; label: string }
  /** AP-24: Modus dieses Berichts — im MANUAL-Modus sind Zeilen bearbeitbar. */
  timeSource: MonthlyReportTimeSource
  hoursFormat: MonthlyReportHoursFormat
  confirmationText: string
  columns: MonthlyReportColumn[]
  rows: MonthlyReportRow[]
  totals: MonthlyReportTotals
  submission: MonthlyReportSubmissionInfo | null
  locked: boolean
}

export interface SubmitMonthlyReportPayload {
  year: number
  month: number
  /** Entweder pin ODER password zur Re-Authentifizierung. */
  pin?: string
  password?: string
  confirmationAccepted: boolean
}

// ─── AP-24: ein Tag der Zeilenbearbeitung ─────────────────────────────────────

export interface AbsenceTypeLite {
  id: string
  name: string
  color: string | null
}

export interface ReportDayAbsence {
  id: string
  absenceTypeId: string
  durationMinutes: number
  note: string | null
}

export interface LvFieldSchema {
  name: string
  label?: string
  type?: string
}

export interface ReportDay {
  date: string
  weekday: string
  locked: boolean
  hoursEditable: boolean
  hoursFormat: MonthlyReportHoursFormat
  workMinutes: number | null
  breakMinutes: number | null
  stamped: { netMinutes: number; breakMinutes: number } | null
  absence: ReportDayAbsence | null
  absenceCount: number
  absenceEditable: boolean
  absenceTypes: AbsenceTypeLite[]
  lvSchema: LvFieldSchema[]
  lvValues: Record<string, unknown>
  /** AP-26: Uhrzeiten und Montage-/Reisefelder dieses Tages. */
  dayFields: ReportDayFields
}

/**
 * AP-26 — welche der zehn getypten Tagesfelder die Maske zeigt und was drinsteht.
 *
 * **Der Server entscheidet, was sichtbar ist**, und liefert Beschriftung und Art
 * gleich mit: Die Maske gibt es zweimal (ERP-Dialog und PWA-Sheet), ein zweites
 * Label-Verzeichnis im Frontend wäre die nächste Doppelung.
 */
export interface ReportDayFields {
  visible: { key: string; label: string; kind: MonthlyReportColumnKind }[]
  /** Kommt/Geht sind wie die Stunden nur im händischen Modus überschreibbar. */
  timesEditable: boolean
  values: {
    startTime: string
    endTime: string
    travelMinutes: number | null
    departure: string
    returnTime: string
    km: number | null
    isDriver: boolean
    overnight: boolean
    appointment: string
    appointmentMinutes: number | null
  }
}

export interface SaveReportDayPayload {
  date: string
  workMinutes?: number | null
  breakMinutes?: number | null
  customFields?: Record<string, unknown>
  absence?: { absenceTypeId: string; durationMinutes: number; note?: string } | null

  // ── AP-26 ──────────────────────────────────────────────────────────────────
  // Uhrzeiten als Wall-Clock `HH:MM` (der Server setzt sie mit dem Tagesdatum
  // zusammen). `null` = löschen, Feld weglassen = unverändert.
  startTime?: string | null
  endTime?: string | null
  travelMinutes?: number | null
  departure?: string | null
  returnTime?: string | null
  km?: number | null
  isDriver?: boolean
  overnight?: boolean
  appointment?: string | null
  appointmentMinutes?: number | null
}

export const monthlyReportApi = {
  /**
   * AP-26: Spaltenkatalog + gespeicherte Einrichtung in einem Aufruf (ADMIN).
   * Ersetzt die Katalog-Konstante, die der ERP bis dahin selbst führte.
   */
  columnCatalog: () =>
    api.get<MonthlyReportColumnCatalog>('/monthly-report/column-catalog'),

  overview: (year: number, month: number) =>
    api.get<MonthlyReportOverview>('/monthly-report/overview', { params: { year, month } }),

  /** Eigener Monatsbericht (Daten + Spaltenkonfiguration + Abgabestatus). */
  get: (year: number, month: number) =>
    api.get<MonthlyReport>('/monthly-report', { params: { year, month } }),

  /** PDF-Vorschau des eigenen (noch nicht abgegebenen) Berichts. */
  exportPdf: (year: number, month: number) =>
    api.get('/monthly-report/export', { params: { year, month }, responseType: 'arraybuffer' }),

  /** Rechtsverbindliche Abgabe (FES). */
  submit: (payload: SubmitMonthlyReportPayload) =>
    api.post('/monthly-report/submit', payload),

  /** AP-24: Ein Tag mit allem, was die Zeilenbearbeitung braucht. */
  getDay: (date: string, userId?: string) =>
    api.get<ReportDay>('/monthly-report/day', { params: { date, ...(userId ? { userId } : {}) } }),

  /** AP-24: Stunden, Fehlzeit und Diäten eines Tages speichern. */
  saveDay: (payload: SaveReportDayPayload) =>
    api.put<ReportDay>('/monthly-report/day', payload),

  /** AP-24: Monat aus der Zeiterfassung vorbefüllen (nur noch leere Tage). */
  prefill: (year: number, month: number, userId?: string) =>
    api.post<{ data: { filled: number; skipped: number } }>('/monthly-report/prefill', {
      year,
      month,
      ...(userId ? { userId } : {}),
    }),

  setRequired: (userId: string, required: boolean) =>
    api.patch<{ data: { userId: string; required: boolean } }>('/monthly-report/required', {
      userId,
      required,
    }),

  revoke: (id: string) =>
    api.post(`/monthly-report/submissions/${id}/revoke`),

  submissionPdf: (id: string) =>
    api.get(`/monthly-report/submissions/${id}/pdf`, { responseType: 'arraybuffer' }),

  // ── AP-10 Nachtrag 5: dieselben PDFs per E-Mail ──
  /** Heutiger Stand des eigenen Berichts. */
  exportPdfRequest: (year: number, month: number): ExportRequest => ({ url: '/monthly-report/export', params: { year, month } }),
  /** Das eingefrorene PDF einer Abgabe. */
  submissionPdfRequest: (id: string): ExportRequest => ({
    url: `/monthly-report/submissions/${id}/pdf`,
    mailUrl: `/monthly-report/submissions/${id}/mail`,
  }),
}
